/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {
  autoUpdate,
  flip,
  type FloatingFocusManagerProps,
  offset,
  type OffsetOptions,
  type Placement,
  shift,
  size,
  useClick,
  useClientPoint,
  useDismiss,
  useFloating,
  useInteractions,
  type UseInteractionsReturn,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import { tokens } from '@neo4j-ndl/base';
import { useMemo, useState } from 'react';

export interface PopoverOptions {
  isInitialOpen?: boolean;
  placement?: Placement;
  isOpen?: boolean;
  onOpenChange?: (open: boolean, event?: Event) => void;
  offsetOption?: OffsetOptions;
  anchorElement?: Element | null;
  anchorPosition?: {
    // TODO v5: create componentProps type for this
    // oxlint-disable-next-line id-length
    x: number;
    // TODO v5: create componentProps type for this
    // oxlint-disable-next-line id-length
    y: number;
  };
  // TODO v5: fix boolean naming convention
  // eslint-disable-next-line @typescript-eslint/naming-convention
  anchorElementAsPortalAnchor?: boolean;
  shouldCaptureFocus?: boolean;
  initialFocus?: number | React.RefObject<HTMLElement | null>;
  role?:
    | 'tooltip'
    | 'dialog'
    | 'alertdialog'
    | 'menu'
    | 'listbox'
    | 'grid'
    | 'tree';
  // TODO v5: decide if this naming is okay, or change it
  // eslint-disable-next-line @typescript-eslint/naming-convention
  closeOnClickOutside?: boolean;
  // TODO v5: decide if this naming is okay, or change it
  // eslint-disable-next-line @typescript-eslint/naming-convention
  closeOnReferencePress?: boolean;
  shouldCloseOnFocusOut?: boolean;
  returnFocus?: FloatingFocusManagerProps['returnFocus'];
  strategy?: 'fixed' | 'absolute';
  isPortaled?: boolean;
  shouldConstrainSize?: boolean;
}

export interface UsePopoverReturn
  extends UseInteractionsReturn, ReturnType<typeof useFloating> {
  isOpen: boolean;
  labelId: string | undefined;
  descriptionId: string | undefined;
  transitionStyles: React.CSSProperties;
  setLabelId: (labelId: string | undefined) => void;
  setDescriptionId: (descriptionId: string | undefined) => void;
  // TODO v5: fix boolean naming convention
  // eslint-disable-next-line @typescript-eslint/naming-convention
  anchorElementAsPortalAnchor: boolean | undefined;
  shouldCaptureFocus: boolean | undefined;
  shouldCloseOnFocusOut: boolean | undefined;
  initialFocus: number | React.RefObject<HTMLElement | null> | undefined;
  isPortaled: boolean;
  returnFocus: FloatingFocusManagerProps['returnFocus'];
}

export function usePopover({
  isInitialOpen = false,
  placement = 'bottom',
  isOpen: controlledOpen,
  onOpenChange: setControlledOpen,
  offsetOption = 10,
  anchorElement,
  anchorPosition,
  anchorElementAsPortalAnchor,
  shouldCaptureFocus,
  initialFocus,
  role: roleProp,
  closeOnClickOutside,
  closeOnReferencePress,
  shouldCloseOnFocusOut,
  returnFocus,
  strategy = 'absolute',
  isPortaled = true,
  shouldConstrainSize = false,
}: PopoverOptions = {}): UsePopoverReturn {
  // Internal state for uncontrolled mode (no `isOpen` prop provided).
  const [isOpenUncontrolled, setIsOpenUncontrolled] = useState(isInitialOpen);
  const [labelId, setLabelId] = useState<string | undefined>();
  const [descriptionId, setDescriptionId] = useState<string | undefined>();

  // Controlled/uncontrolled pattern: prefer the consumer-provided `isOpen`,
  // falling back to internal state when not provided.
  const isOpen = controlledOpen ?? isOpenUncontrolled;

  const data = useFloating({
    elements: {
      reference: anchorElement,
    },
    middleware: [
      offset(offsetOption),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'end',
        padding: 5,
      }),
      shift(),
      ...(shouldConstrainSize
        ? [
            size({
              apply({ availableHeight, availableWidth, elements }) {
                Object.assign(elements.floating.style, {
                  maxHeight: `${availableHeight}px`,
                  maxWidth: `${availableWidth}px`,
                });
              },
              padding: 5,
            }),
          ]
        : []),
    ],
    // Floating UI calls this when interactions (useClick, useDismiss, etc.)
    // trigger an open/close. We always sync internal state so uncontrolled
    // mode works, and notify the consumer callback exactly once if provided.
    onOpenChange: (open, event) => {
      setIsOpenUncontrolled(open);
      setControlledOpen?.(open, event);
    },
    open: isOpen,
    placement: placement,
    strategy,
    whileElementsMounted: autoUpdate,
  });
  const context = data.context;

  // Only enable built-in click-to-toggle in uncontrolled mode. In controlled
  // mode the consumer owns the open state and wires up their own trigger.
  const click = useClick(context, {
    enabled: controlledOpen === undefined,
  });

  const dismiss = useDismiss(context, {
    outsidePress: closeOnClickOutside,
    referencePress: closeOnReferencePress,
  });
  const role = useRole(context, {
    role: roleProp,
  });

  const clientPoint = useClientPoint(context, {
    enabled: anchorPosition !== undefined,
    // oxlint-disable-next-line id-length
    x: anchorPosition?.x,
    // oxlint-disable-next-line id-length
    y: anchorPosition?.y,
  });

  const interactions = useInteractions([click, dismiss, role, clientPoint]);

  const { styles: transitionStyles } = useTransitionStyles(context, {
    duration: Number.parseInt(tokens.motion.duration.quick) ?? 0,
  });

  return useMemo(
    () => ({
      ...data,
      ...interactions,
      anchorElementAsPortalAnchor,
      descriptionId,
      initialFocus,
      isOpen,
      isPortaled,
      labelId,
      returnFocus,
      setDescriptionId,
      setLabelId,
      shouldCaptureFocus,
      shouldCloseOnFocusOut,
      transitionStyles,
    }),
    [
      isOpen,
      interactions,
      data,
      transitionStyles,
      labelId,
      descriptionId,
      anchorElementAsPortalAnchor,
      shouldCaptureFocus,
      shouldCloseOnFocusOut,
      initialFocus,
      isPortaled,
      returnFocus,
    ],
  );
}
